// The measure's status on the iPad's Measure and PM assess tabs (measure-status.ts;
// mock-up docs/mockups/approved-hours-status.html): a label for CRM lists, never a
// gate. "Measuring done" readies it for the PM; "Assessment done" marks it assessed.
import { formatDate, isoToday } from '../lib/dates';
import type { DeviceRecord } from '../lib/device-db';
import { statusAction, statusOf, type MeasureStatus } from '../lib/measure-status';
import { saveStatusTap } from '../lib/measure-status-save';

const CHIP: Record<MeasureStatus, string> = {
  Measuring: 'bg-sky-100 text-sky-900',
  'Ready to assess': 'bg-amber-100 text-amber-900',
  Assessing: 'bg-violet-100 text-violet-900',
  Assessed: 'bg-green-100 text-green-900',
  'In review': 'bg-slate-200 text-slate-800',
  Approved: 'bg-slate-200 text-slate-800',
};

const ACTION = {
  'measuring-done': { prompt: 'Finished measuring?', label: 'Measuring done', primary: true },
  'back-to-measuring': { prompt: 'More to measure?', label: 'Back to measuring', primary: false },
  'assessment-done': { prompt: 'Done assessing every area?', label: 'Assessment done', primary: true },
} as const;

export function MeasureStatusBar({ measure, tab }: { measure: DeviceRecord; tab: 'measure' | 'assess' }) {
  const status = statusOf(measure.fields.hig_status);
  const action = statusAction(status, tab);
  const measured = formatDate(measure.fields.hig_measuredon);
  const assessed = formatDate(measure.fields.hig_assessedon);
  const days = [measured && `measured ${measured}`, assessed && `assessed ${assessed}`].filter(Boolean);

  // Applied to the status stored now, so a tap on a screen another device has overtaken changes nothing.
  const act = () => action && void saveStatusTap(measure, action, isoToday());

  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${CHIP[status]}`}>{status}</span>
      {days.length > 0 && <span className="text-slate-600">{days.join(' · ')}</span>}
      {action && (
        <span className="ml-auto flex items-center gap-2">
          <span className="text-slate-500">{ACTION[action].prompt}</span>
          <button
            type="button"
            onClick={act}
            className={`min-h-11 rounded-lg px-4 font-semibold ${ACTION[action].primary ? 'bg-primary text-primary-foreground' : 'border border-slate-300 bg-white text-slate-800'}`}
          >
            {ACTION[action].label}
          </button>
        </span>
      )}
    </div>
  );
}
